import chalk from 'chalk';
import { getApiKey, getApiEndpoint } from './config.js';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

// Validate API key
export async function validateApiKey(apiKey?: string): Promise<boolean> {
  try {
    const key = apiKey || await getApiKey();
    const endpoint = getApiEndpoint();
    
    const response = await fetch(`${endpoint}/api/validate`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${key}`,
      },
    });
    
    if (response.ok) {
      const result = await response.json() as { valid?: boolean };
      return result.valid === true;
    }
    
    return false;
  } catch (error) {
    return false;
  }
}

// Send usage data
export async function sendUsageData(records: any[], apiKey?: string): Promise<ApiResponse> {
  try {
    const key = apiKey || await getApiKey();
    const endpoint = getApiEndpoint();
    
    const response = await fetch(`${endpoint}/api/usage`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${key}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ records }),
    });
    
    const result = await response.json() as { error?: string; [key: string]: any };
    
    if (!response.ok) {
      return {
        success: false,
        error: result.error || `HTTP ${response.status}`,
      };
    }
    
    return {
      success: true,
      data: result,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Network error',
    };
  }
}

// Fetch ranking data
export async function fetchRankingData(period: string, apiKey?: string): Promise<ApiResponse> {
  try {
    const key = apiKey || await getApiKey();
    const endpoint = getApiEndpoint();
    
    const response = await fetch(`${endpoint}/api/ranking?period=${period}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${key}`,
      },
    });
    
    const result = await response.json() as { error?: string; rankings?: any[]; userRanking?: any };
    
    if (!response.ok) {
      return {
        success: false,
        error: result.error || `HTTP ${response.status}`,
      };
    }
    
    return {
      success: true,
      data: result,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Network error',
    };
  }
}

// Test API connection
export async function testApiConnection(apiKey?: string): Promise<boolean> {
  console.log(chalk.gray('\n🔍 Testing API connection...'));
  
  const isValid = await validateApiKey(apiKey);
  
  if (isValid) {
    console.log(chalk.green('✅ API connection successful!'));
    return true;
  } else {
    console.log(chalk.red('❌ API connection failed'));
    console.log(chalk.yellow('\nPlease check the following:'));
    console.log('1. Is the API key correct?');
    console.log('2. Is the VibeTime server running?');
    console.log('3. Is the network connection working?');
    return false;
  }
}